import { Dispatch } from 'react';
import { useParams } from 'react-router-dom';

import {
	extractTitle,
	useContentOptionsModal,
	ContentListAction,
	useContentAction,
} from '@_features/content';
import { ISearchedContent } from '@_entities/content';
import {
	FolderFilledIcon,
	SelectEmptyIcon,
	SelectCheckedIcon,
	FolderIcon,
	MeatBallIcon,
	PinIcon,
} from '@_shared/icons';
import { Typography } from '@_shared/ui/Typography';

import * as S from './ContentListItem.style';

interface Props {
	isAllFolderPage: boolean;
	isEditMode: boolean;
	isPinned: boolean;
	isContentSelected: (id: number) => boolean;
	data: ISearchedContent;
	dispatch: Dispatch<ContentListAction>;
}

export function ContentListItem({
	isAllFolderPage,
	isEditMode,
	data,
	isPinned,
	isContentSelected,
	dispatch,
}: Props) {
	const { id } = useParams();
	const { openContentOptionsModal } = useContentOptionsModal();
	const { dispatch: dispatchContentAction } = useContentAction(data);

	return (
		<S.Container
			isEditMode={isEditMode}
			to={`/content/${data.id}`}
			onClick={(e) => {
				if (isEditMode) {
					e.stopPropagation();
					e.preventDefault();
					dispatch({
						type: 'SELECT_CONTENT',
						contentId: data.id,
						folderId: data.folderId,
					});
				}
			}}
			isDeleteInclude={isContentSelected(data.id)}
		>
			{isEditMode && (
				<S.SelectIcon>
					{isContentSelected(data.id) ? <SelectCheckedIcon /> : <SelectEmptyIcon />}
				</S.SelectIcon>
			)}

			<S.TextContainer>
				{!isEditMode && (
					<S.FolderTextRow>
						<S.TextRow>
							{isAllFolderPage && (
								<>
									<FolderIcon />
									<Typography type="body-medium" color="font-tertiary">
										{data.folderName}
									</Typography>
								</>
							)}
						</S.TextRow>
						<S.FolderOptionIconWrapper>
							<MeatBallIcon
								onClick={(e) => {
									e.stopPropagation();
									e.preventDefault();
									openContentOptionsModal(data, true, false, data.title);
								}}
							/>
						</S.FolderOptionIconWrapper>
					</S.FolderTextRow>
				)}

				{id === undefined && (
					<S.TextRow>
						<FolderFilledIcon />
						<Typography type="body-medium" color="font-secondary">
							{data.folderName}
						</Typography>
					</S.TextRow>
				)}

				{/* title */}
				<Typography type="title-medium" color="font-primary" noOfLine={2}>
					{extractTitle(data)}
				</Typography>

				{/* memo */}
				{data.memo && (
					<Typography type="body-medium" color="font-tertiary" noOfLine={1}>
						{data.memo}
					</Typography>
				)}

				{/* createDate & siteName */}
				<S.TextRow>
					<Typography type="body-medium" color="font-tertiary">
						{data.createdDate}
					</Typography>
					<Typography type="body-medium" color="font-tertiary">
						|
					</Typography>
					<Typography type="body-medium" color="font-tertiary">
						{data.siteName}
					</Typography>
				</S.TextRow>
			</S.TextContainer>
			<S.RightContainer>
				{data.thumbnailURL && <S.Thumbnail src={data.thumbnailURL} />}
				<S.PinWrapper
					isPinned={isPinned}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						dispatchContentAction({
							type: 'PIN_CONTENT',
							contentFixed: isPinned,
							pinType: isAllFolderPage ? 'entire' : 'folder',
						});
					}}
				>
					<PinIcon />
				</S.PinWrapper>
			</S.RightContainer>
		</S.Container>
	);
}
